import type { FormLocale } from '@jlg-website/shared';
import { ChevronLeft, ChevronRight } from '@/components/pages/icons';
import { textFor } from '../shared/copy';
import { CalendarMonth } from './calendar-month';
import { CalendarDays } from './icons';
import { formatMonthLabel } from '../shared/date-time';
import { listTimeZoneOptions } from '../shared/time-zones';
import type { CalendarDay } from './type';

type DateStepProps = {
  month: string;
  days: CalendarDay[];
  selectedDate: string | null;
  timeZone: string;
  locale: FormLocale;
  canGoToPreviousMonth: boolean;
  onSelectDate: (date: string) => void;
  onChangeTimeZone: (timeZone: string) => void;
  onChangeMonth: (offset: number) => void;
};

export function DateStep({
  month,
  days,
  selectedDate,
  timeZone,
  locale,
  canGoToPreviousMonth,
  onSelectDate,
  onChangeTimeZone,
  onChangeMonth,
}: DateStepProps) {
  return (
    <div className="flex flex-col gap-3">
      {/* Both labels of this step carry the weight of the question above them, so the visitor
          reads one list of asks rather than a question followed by two captions. */}
      <label className="text-base font-bold text-brand-text-button" htmlFor="callback-time-zone">
        {textFor(locale, 'timeZone')}
      </label>

      <div className="flex flex-col gap-2 @lg:flex-row @lg:items-center">
        <select
          id="callback-time-zone"
          value={timeZone}
          onChange={(event) => onChangeTimeZone(event.target.value)}
          className="flex-1 cursor-pointer rounded-lg border border-brand-muted-light bg-white px-3 py-2.5 text-sm text-brand-text-button"
        >
          {listTimeZoneOptions(timeZone).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <div className="flex items-center gap-2 rounded-lg border border-brand-muted-light px-3 py-2.5">
          <CalendarDays className="size-4 shrink-0 text-brand-accent" aria-hidden="true" />
          <span className="flex-1 text-sm font-medium text-brand-text-button">
            {formatMonthLabel(locale, month)}
          </span>

          <button
            type="button"
            onClick={() => onChangeMonth(-1)}
            disabled={!canGoToPreviousMonth}
            aria-label={textFor(locale, 'previousMonth')}
            className="cursor-pointer text-brand-primary disabled:cursor-not-allowed disabled:text-gray-light"
          >
            <ChevronLeft className="size-4" />
          </button>

          <button
            type="button"
            onClick={() => onChangeMonth(1)}
            aria-label={textFor(locale, 'nextMonth')}
            className="cursor-pointer text-brand-primary"
          >
            <ChevronRight className="size-4" />
          </button>
        </div>
      </div>

      <p className="text-base font-bold text-brand-text-button">{textFor(locale, 'whenToCall')}</p>

      <CalendarMonth
        month={month}
        days={days}
        selectedDate={selectedDate}
        locale={locale}
        onSelect={onSelectDate}
      />
    </div>
  );
}
